import { Injectable } from '@angular/core';
import { QueryEntity } from '@datorama/akita';

import { ClientState, ClientStore } from './client.store';
import { Client } from '@core/models/client.model';
import { AppStoreQuery } from '@core/state';
import { ClientDetailsQuery } from '@core/client-details/client-details.query';
// import { combineLatest } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ClientQuery extends QueryEntity<ClientState> {
  constructor(
    protected store: ClientStore,
    protected appStoreQuery: AppStoreQuery,
    public readonly clientDetailsQuery: ClientDetailsQuery,
  ) {
    super(store);
  }

  shouldShowWPHG() {
    const codename = (this.getActive() as Client)?.clientClass;
    const type = (this.getActive() as Client)?.type;
    const isEligible = (this.getActive() as Client)?.isEligible;
    const isProfessional = (this.getActive() as Client)?.isProfessional;
    const businessType = (this.getActive() as Client)?.businessType;
    const isBalanceSheetFilledOut = (this.getActive() as Client)?.isBalanceSheetFilledOut;
    return (
      type === 'person' ||
      (codename !== null && !this.isEligible()) ||
      (isBalanceSheetFilledOut && businessType !== null && !isProfessional && !isEligible)
    );
  }

  isEligible() {
    const codename = (this.getActive() as Client)?.clientClass;
    return ['eligible-counterparty', 'professional-investors'].includes(codename);
  }

  shouldShowSummary() {
    const clientClass = (this.getActive() as Client)?.clientClass;
    const validClientClasses = [
      'eligible-counterparty',
      'professional-investors',
      'elective-professionals',
      'semi-professionals',
      'retail-investor',
    ];
    return (
      clientClass !== null &&
      clientClass !== undefined &&
      clientClass !== '' &&
      typeof clientClass === 'string' &&
      validClientClasses.includes(clientClass)
    );
  }
  /**
   * Determines if the waiver rights section should be shown in the onboarding nav header.
   * Keeps the waiver tab visible for semi/elective professionals throughout onboarding,
   * including after submit, where the waiver screen becomes read-only.
   */
  shouldShowWaiver() {
    let canViewWaiver = false;
    let codename = null;
    this.appStoreQuery.selectUser().subscribe((user) => {
      canViewWaiver = !!user?.isClient || !!user?.isAdvisor;
      codename = (this.getActive() as Client)?.clientClass;
    });

    if (!canViewWaiver || (codename !== 'semi-professionals' && codename !== 'elective-professionals')) {
      return false;
    }
    return true;
  }

  isCompany(): boolean {
    return 'company' === (this.getActive() as Client)?.type;
  }

  hasUserLoggedIn(): boolean {
    return (this.getActive() as Client)?.hasUserLoggedIn;
  }

  hasUserInvited(): boolean {
    return (this.getActive() as Client)?.hasUserInvited;
  }

  isProfessional(): boolean {
    return (this.getActive() as Client)?.isProfessional;
  }

  isSemi(): boolean {
    return (this.getActive() as Client)?.isSemi;
  }

  isElective(): boolean {
    return (this.getActive() as Client)?.clientClass === 'elective-professionals';
  }

  isAdvised(): boolean {
    return !!(this.getActive() as Client)?.advisorUser;
  }

  isEligibleCounterParty(): boolean {
    return (this.getActive() as Client)?.clientClass === 'eligible-counterparty';
  }

  isSemiProfessional(): boolean {
    return (this.getActive() as Client)?.clientClass === 'semi-professionals';
  }

  isProfessionalInvestor(): boolean {
    return (this.getActive() as Client)?.clientClass === 'professional-investors';
  }

  isRetailInvestor(): boolean {
    return (this.getActive() as Client)?.clientClass === 'retail-investor';
  }

  hasClientClass(): boolean {
    return (this.getActive() as Client)?.clientClass !== null;
  }
}
